import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Spinner } from '../components/ui/Icon.js';
import { useBrand } from '../lib/brand-context.js';
import { BrandList } from '../components/settings/BrandList.js';
import { TagManager } from '../components/settings/TagManager.js';
import { CompetitorManager } from '../components/settings/CompetitorManager.js';
import { TenantSettingsPanel } from '../components/settings/TenantSettings.js';
import { SearchConsoleIntegrationsSection } from '../components/SearchConsoleIntegrationsSection.js';
import { Tabs } from '../components/Tabs.js';
import type { TabItem } from '../components/Tabs.js';

type SettingsTab = 'brands' | 'tags' | 'competitors' | 'integrations' | 'tenant';

const TABS: readonly TabItem<SettingsTab>[] = [
  { id: 'brands', label: 'Brands', icon: 'bx-building-house' },
  { id: 'tags', label: 'Tags', icon: 'bx-purchase-tag' },
  { id: 'competitors', label: 'Competitors', icon: 'bx-group' },
  { id: 'integrations', label: 'Integrations', icon: 'bx-plug' },
  { id: 'tenant', label: 'Tenant settings', icon: 'bx-cog' },
] as const;

const VALID_TABS: readonly SettingsTab[] = ['brands', 'tags', 'competitors', 'integrations', 'tenant'];

export function SettingsPage() {
  const { user, activeTenantId, selectedBrand, bootstrapTenant } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  // Initial tab from ?tab= query param (089b deep-link support); falls back to 'brands'.
  // Also accept legacy #integrations hash.
  const initialTab: SettingsTab = (() => {
    const param = searchParams.get('tab');
    if (param && (VALID_TABS as readonly string[]).includes(param)) {
      return param as SettingsTab;
    }
    if (typeof window !== 'undefined' && window.location.hash === '#integrations') {
      return 'integrations';
    }
    return 'brands';
  })();
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  // Sync activeTab back to the URL so deep-links survive copy-paste / refresh.
  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    // Clear OAuth-callback params once the user navigates away from integrations.
    if (tab !== 'integrations') {
      next.delete('gsc_connect');
    }
    setSearchParams(next, { replace: true });
  };

  // 089b: detect OAuth callback success and pin to integrations tab.
  const gscConnect = searchParams.get('gsc_connect');
  useEffect(() => {
    if (gscConnect === 'success' && activeTab !== 'integrations') {
      setActiveTab('integrations');
    }
  }, [gscConnect, activeTab]);

  // Spec 076 Pass 1.5: if no brand is selected yet, obtain a tenant id via
  // bootstrapTenant() so sub-components can target /api/v1/tenants/:id/...
  useEffect(() => {
    if (activeTenantId || bootstrapping) return;
    let cancelled = false;
    setBootstrapping(true);
    setBootstrapError(null);
    bootstrapTenant()
      .catch((err: unknown) => {
        if (cancelled) return;
        setBootstrapError(
          err instanceof Error ? err.message : 'Failed to prepare workspace',
        );
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });
    return () => {
      cancelled = true;
    };
    // Intentionally only depend on activeTenantId — once it resolves we stop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  if (!user) {
    return (
      <div className="hub-page--reading">
        <h1 className="hub-page__title">Settings</h1>
        <p className="mt-2 text-sm text-slate-500">Loading user information...</p>
      </div>
    );
  }

  if (!activeTenantId) {
    return (
      <div className="hub-page--reading">
        <h1 className="hub-page__title">Settings</h1>
        {bootstrapError ? (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <p className="font-medium">Unable to load workspace</p>
            <p className="mt-1">{bootstrapError}</p>
            <button
              type="button"
              onClick={() => {
                setBootstrapError(null);
                setBootstrapping(true);
                bootstrapTenant()
                  .catch((err: unknown) => {
                    setBootstrapError(
                      err instanceof Error ? err.message : 'Failed to prepare workspace',
                    );
                  })
                  .finally(() => setBootstrapping(false));
              }}
              className="hub-btn hub-btn--ghost mt-2"
            >
              Retry
            </button>
          </div>
        ) : (
          <p className="mt-2 flex items-center gap-2 text-sm text-slate-500">
            <Spinner  />
            Preparing your workspace...
          </p>
        )}
      </div>
    );
  }

  const tenantId = activeTenantId;

  return (
    <div className="hub-page--reading">
      <h1 className="hub-page__title">Settings</h1>
      <p className="mt-1 text-sm text-slate-500">
        Manage brands, tags, competitors, and tenant configuration.
      </p>

      <div className="mt-6">
        <Tabs tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} ariaLabel="Settings tabs" />
      </div>

      {/* Tab content — one .hub-section, since exactly one tab body renders at a time. */}
      <div className="hub-section">
        {activeTab === 'brands' && <BrandList tenantId={tenantId} />}
        {activeTab === 'tags' && <TagManager tenantId={tenantId} />}
        {activeTab === 'competitors' && <CompetitorManager tenantId={tenantId} />}
        {activeTab === 'integrations' && (
          <IntegrationsTabBody
            brandId={selectedBrand?.id ?? null}
            tenantId={tenantId}
            showPicker={gscConnect === 'success'}
            onPickerComplete={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('gsc_connect');
              setSearchParams(next, { replace: true });
            }}
          />
        )}
        {activeTab === 'tenant' && <TenantSettingsPanel tenantId={tenantId} />}
      </div>
    </div>
  );
}

/**
 * Integrations tab body — currently lists Search Console (089b). Future
 * integrations (089c+) will land alongside in the same section.
 *
 * Requires a brand to be selected so the underlying connection / metrics
 * routes can resolve. If no brand is selected we show a prompt instead.
 */
function IntegrationsTabBody({
  brandId,
  tenantId,
  showPicker,
  onPickerComplete,
}: {
  brandId: string | null;
  tenantId: string | null;
  showPicker: boolean;
  onPickerComplete?: () => void;
}) {
  if (!brandId) {
    return (
      <div className="hub-plate hub-plate--flat p-6 text-sm text-[var(--hub-color-ink-muted)]">
        Choose a brand in the bar above to manage its integrations.
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <SearchConsoleIntegrationsSection
        brandId={brandId}
        tenantId={tenantId}
        showPicker={showPicker}
        onPickerComplete={onPickerComplete}
      />
    </div>
  );
}
